import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Segmented, Space, Switch, Tabs } from 'antd';
import useChatFeature from '../hooks/useChatFeature';
import useSandbox from '../hooks/useSandbox';
import { getSettings, updateSettings } from '../services/SettingsService';
import { browserEmailLocale, getNotificationSettings, updateNotificationSettings } from '../services/NotificationsService';
import { getThemeMode, setThemeMode } from '../utils/themeMode';
import { setPresencePrivacy } from '../utils/presenceClient';
import { formatRelativeTime } from '../utils/relativeTime';
import MyBackups from '../components/sandbox/MyBackups';
import { ManageSandboxSection } from '../components/sandbox/ManageSandbox';
import DockerImageCache from '../components/sandbox/DockerImageCache';
import SandboxTypePicker from '../components/sandbox/SandboxTypePicker';
import ModelKeyForm from '../components/sandbox/ModelKeyForm';
import { PlanChangeButton, currentPlan, usePlanRequests } from '../components/sandbox/PlanRequest';
import NotificationSettings from '../components/settings/NotificationSettings';
import ProjectTabsSettings from '../components/settings/ProjectTabsSettings';
import miaPlatformDataService from '../services/MiaPlatformDataService';
import miaFlowAuth from '../auth/MiaFlowAuth';
import { modelChoices } from '../components/sandbox/modelChoices';
import { FLOW_DOCS_URL } from '../constants/helpLinks';
import '../components/sandbox/sandbox.css';
import './SandboxHome.css';
import './UserSettings.css';

// The tabs, and the sections each holds (the `/settings#set-…` links of the search and elsewhere open their tab).
export const SETTINGS_TABS = [
  { key: 'general', label: 'General', sections: ['set-build', 'set-git', 'set-home', 'set-look', 'set-help'] },
  { key: 'projects', label: 'Projects', sections: ['set-tabs', 'set-collab'] },
  { key: 'notifications', label: 'Notifications', sections: ['set-email', 'set-notify'] },
  { key: 'sandbox', label: 'Sandbox and Backups', sections: ['set-sandbox', 'set-sandbox-type', 'set-backups'] },
];

/** The tab of `#projects` or of a section's `#set-collab`; General otherwise. */
export function settingsTabOf(hash) {
  const id = decodeURIComponent(String(hash || '').replace(/^#/, ''));
  return (SETTINGS_TABS.find((item) => item.key === id || item.sections.includes(id)) || SETTINGS_TABS[0]).key;
}

/**
 * The person's preferences (sandbox-first, docs/SANDBOX_FIRST.md), opened from Settings next to their avatar: how they
 * build (AI tool, Flow's assistant), what Home shows, the theme, their sandbox's plan, and Help (Flow's documentation,
 * formerly the sidebar footer's "?"). Choices are the person's
 * own settings (`/api/v1/me/settings`), so they follow them to any browser.
 */
export default function UserSettings() {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const tab = settingsTabOf(hash);
  // `/settings#set-look` (the global search): its tab, and that section in view.
  useEffect(() => {
    if (!hash) { return; }
    const id = decodeURIComponent(hash.slice(1));
    if (SETTINGS_TABS.some((item) => item.key === id)) { return; }
    // Again while the sections above it load and push it down (2026-10-31: Backups from a project's menu landed on
    // the top of the Sandbox tab).
    const timers = [50, 400, 1000, 2000].map((ms) => setTimeout(() => document.getElementById(id)?.scrollIntoView?.({ block: 'start' }), ms));
    return () => timers.forEach(clearTimeout);
  }, [hash]);
  const [chatOn, setChatOn, chatAvailable] = useChatFeature();
  const [settings, setSettings] = useState(null);
  const [theme, setTheme] = useState(getThemeMode);
  const [saveError, setSaveError] = useState(null);
  const { sandbox, refresh: refreshSandbox } = useSandbox({ intervalMs: 30000 });
  // The plan from the sandbox admin, as the admin page, plan requests and Home show it (one source of truth).
  const [planRequests] = usePlanRequests();
  const plan = currentPlan(sandbox, planRequests);
  const { org, own } = modelChoices(sandbox);
  const [ownKey, setOwnKey] = useState(undefined);
  const ownSaved = own.available && Boolean(ownKey?.configured);

  useEffect(() => {
    getSettings().then((value) => value && setSettings(value)).catch(() => {});
  }, []);

  // Sharing emails: hidden when the backend does not know them (older backend, or the call failed).
  const [notifications, setNotifications] = useState(null);
  useEffect(() => {
    let active = true;
    getNotificationSettings().then((value) => {
      if (!active || !value) { return; }
      setNotifications(value);
      // No language chosen yet: the browser's, once and quietly.
      if (value.locale == null) { updateNotificationSettings({ locale: browserEmailLocale() }).catch(() => {}); }
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  // Notifications per event, how emails come, per project (components/settings/NotificationSettings.jsx).
  const saveNotify = (partial) => {
    setSaveError(null);
    const previous = notifications;
    setNotifications((current) => (current ? {
      ...current,
      notify: {
        ...current.notify,
        ...(partial.email ? { email: partial.email } : {}),
        events: { ...current.notify?.events, ...(partial.events || {}) },
        projects: Object.fromEntries(Object.entries({ ...current.notify?.projects, ...(partial.projects || {}) }).filter(([, level]) => level !== 'all')),
      },
    } : current));
    updateNotificationSettings({ notify: partial })
      .then((value) => { if (value?.notify) { setNotifications(value); } })
      .catch(() => {
        setNotifications(previous);
        setSaveError('Your change could not be saved. Try again in a moment.');
      });
  };
  // The person's projects, for the per-project choice.
  const [projectChoices, setProjectChoices] = useState([]);
  const hasNotify = Boolean(notifications?.notify);
  useEffect(() => {
    if (!hasNotify) { return undefined; }
    let active = true;
    const tenant = miaFlowAuth.getSelectedTenant?.();
    const tenantId = tenant?.tenantId || tenant?.id || null;
    Promise.resolve(tenantId ? miaPlatformDataService.getProjects(tenantId, null, 1, 100) : null)
      .then((result) => { if (active) { setProjectChoices((result?.data || []).map((p) => ({ id: p._id, name: p.name }))); } })
      .catch(() => {});
    return () => { active = false; };
  }, [hasNotify]);

  const saveShareEmails = (value) => {
    setSaveError(null);
    const previous = notifications;
    setNotifications({ ...previous, shareEmails: value });
    updateNotificationSettings({ shareEmails: value, locale: browserEmailLocale() }).catch(() => {
      setNotifications(previous);
      setSaveError('Your change could not be saved. Try again in a moment.');
    });
  };

  const save = (partial) => {
    setSaveError(null);
    setSettings((current) => (current ? {
      ...current,
      ...Object.fromEntries(Object.entries(partial).map(([section, part]) => [section, { ...current[section], ...part }])),
    } : current));
    updateSettings(partial).catch(() => setSaveError('Your change could not be saved. Try again in a moment.'));
  };

  const tools = sandbox?.aiTools || [];
  // Live presence in shared projects: on unless the person turned it off (settings `collab`, utils/presenceClient.js).
  const collab = {
    presence: settings?.collab?.presence !== false,
    hideCursor: Boolean(settings?.collab?.hideCursor),
    followable: settings?.collab?.followable !== false,
    typingOnly: Boolean(settings?.collab?.typingOnly),
  };
  const saveCollab = (partial) => {
    save({ collab: partial });
    setPresencePrivacy(partial);
  };

  const panes = {
    general: (
    <>
      <section aria-labelledby="set-build" className="sbx-card settings-section">
        <h2 id="set-build">How you build</h2>
        <div className="settings-row">
          <span className="settings-label">
            <strong>Your AI tool</strong>
            <span>{tools.length ? `Connected: ${tools.map((tool) => `${tool.label} (${formatRelativeTime(tool.lastSeenAt)})`).join(', ')}` : 'No AI tool has connected to your sandbox yet.'}</span>
          </span>
          <Button onClick={() => navigate('/welcome')}>{tools.length ? 'Connect another' : 'Connect'}</Button>
        </div>
        {/* Not in the person's plan: no switch to turn Chat on. */}
        {chatAvailable && (
          <div className="settings-row">
            <label className="settings-label" htmlFor="set-chat">
              <strong>Flow’s assistant (Chat)</strong>
              <span>
                {ownSaved ? 'Chat, agents and playbooks in Flow, with your own model key.'
                  : org.available ? 'Chat, agents and playbooks in Flow, with the models your organization provides.'
                    : own.available ? 'Needs your own model key: save it below.' : `Not available: ${org.note}`}
              </span>
            </label>
            <Switch checked={chatOn} disabled={!org.available && !ownSaved && !chatOn} id="set-chat" onChange={(value) => setChatOn(value)} />
          </div>
        )}
        <div className="settings-row settings-row-stack">
          <span className="settings-label">
            <strong>Your own model key</strong>
            <span>{own.available ? 'For Flow’s assistant, from your model provider. Used only for your chats.' : own.note}</span>
          </span>
          {own.available && <ModelKeyForm idPrefix="set-key" onChange={setOwnKey} />}
        </div>
        <div className="settings-row">
          <span className="settings-label">
            <strong>First-run setup</strong>
            <span>{settings?.onboarding?.completedAt ? `Done ${formatRelativeTime(settings.onboarding.completedAt)}.` : 'Choose how you build, step by step.'}</span>
          </span>
          <Button onClick={() => navigate('/welcome')}>Run it again</Button>
        </div>
      </section>

      <section aria-labelledby="set-git" className="sbx-card settings-section">
        <h2 id="set-git">Git accounts</h2>
        <div className="settings-row">
          <span className="settings-label">
            <strong>GitHub and GitLab</strong>
            <span>Connect your accounts so Flow, its agents and your AI tool can clone, pull and push your private repositories. Tokens stay in OpenBao.</span>
          </span>
          <Button onClick={() => navigate('/features/connections#your-accounts')}>Manage accounts</Button>
        </div>
      </section>

      <section aria-labelledby="set-home" className="sbx-card settings-section">
        <h2 id="set-home">Home</h2>
        <div className="settings-row">
          <label className="settings-label" htmlFor="set-checklist">
            <strong>Getting-started list</strong>
            <span>The first things worth doing: connect your AI tool, create a project, see it running, try Chat, share.</span>
          </label>
          <Switch
            checked={!settings?.home?.checklistDismissed}
            disabled={!settings}
            id="set-checklist"
            onChange={(value) => save({ home: { checklistDismissed: !value } })}
          />
        </div>
      </section>

      <section aria-labelledby="set-look" className="sbx-card settings-section">
        <h2 id="set-look">Appearance</h2>
        <div className="settings-row">
          <span className="settings-label"><strong>Theme</strong><span>Kept in this browser.</span></span>
          <Segmented
            onChange={(value) => { setThemeMode(value); setTheme(value); }}
            options={[{ value: 'light', label: 'Light' }, { value: 'dark', label: 'Dark' }]}
            value={theme}
          />
        </div>
      </section>

      <section aria-labelledby="set-help" className="sbx-card settings-section">
        <h2 id="set-help">Help</h2>
        <div className="settings-row">
          <span className="settings-label">
            <strong>Documentation</strong>
            <span>Guides and reference for Flow, on docs.mia-platform.eu.</span>
          </span>
          <Button href={FLOW_DOCS_URL} rel="noopener noreferrer" target="_blank">Open documentation</Button>
        </div>
      </section>
    </>
  ),
    projects: (
    <>
      <ProjectTabsSettings />

      <section aria-labelledby="set-collab" className="sbx-card settings-section">
        <h2 id="set-collab">Collaboration and privacy</h2>
        <div className="settings-row">
          <label className="settings-label" htmlFor="set-presence">
            <strong>Show me to collaborators</strong>
            <span>Your avatar in shared projects: the project header, the file tree, the kanban and your cursor. Off: you are invisible, and you do not see others either (it works both ways).</span>
          </label>
          <Switch checked={collab.presence} disabled={!settings} id="set-presence" onChange={(value) => saveCollab({ presence: value })} />
        </div>
        <div className="settings-row">
          <label className="settings-label" htmlFor="set-cursor">
            <strong>Share my cursor and selection</strong>
            <span>Others see your caret and name flag in the editor. Off: they only see that you have the file open.</span>
          </label>
          <Switch checked={!collab.hideCursor} disabled={!settings || !collab.presence} id="set-cursor" onChange={(value) => saveCollab({ hideCursor: !value })} />
        </div>
        <div className="settings-row">
          <label className="settings-label" htmlFor="set-typing-only">
            <strong>Only while I type</strong>
            <span>Reading quietly does not show: others see which file you are in, your cursor and your pointer on the preview only while you type, and for 30 seconds after.</span>
          </label>
          <Switch checked={collab.typingOnly} disabled={!settings || !collab.presence} id="set-typing-only" onChange={(value) => saveCollab({ typingOnly: value })} />
        </div>
        <div className="settings-row">
          <label className="settings-label" htmlFor="set-followable">
            <strong>Let others follow me</strong>
            <span>They jump to the file and the place you are in, and you see who follows you. Off: nobody can follow you.</span>
          </label>
          <Switch checked={collab.followable} disabled={!settings || !collab.presence} id="set-followable" onChange={(value) => saveCollab({ followable: value })} />
        </div>
        <div className="settings-row">
          <span className="settings-label">
            <strong>Your AI agents show under your name</strong>
            <span>Always, in shared projects: what Flow’s chat or your AI tool writes says who drove it, so teammates can ask the right person.</span>
          </span>
        </div>
      </section>
    </>
  ),
    notifications: notifications ? (
    <>
      {notifications && (
        <section aria-labelledby="set-email" className="sbx-card settings-section">
          <h2 id="set-email">Emails</h2>
          <div className="settings-row">
            <label className="settings-label" htmlFor="set-share-emails">
              <strong>Email me when someone shares a project with me</strong>
              <span>
                {notifications.available
                  ? 'A short heads-up with a link to the project. Sent in English or Italian, by your browser’s language.'
                  : 'Your organization has not turned on sharing emails yet.'}
              </span>
            </label>
            <Switch checked={Boolean(notifications.shareEmails)} id="set-share-emails" onChange={saveShareEmails} />
          </div>
        </section>
      )}

      {notifications?.notify && <NotificationSettings onChange={saveNotify} projects={projectChoices} settings={notifications} />}
    </>
  ) : null,
    sandbox: (
    <>
      <section aria-labelledby="set-sandbox" className="sbx-card settings-section">
        <h2 id="set-sandbox">Your sandbox</h2>
        <div className="settings-row">
          <span className="settings-label">
            <strong>{plan.name ? `${plan.name} plan` : 'Plan'}</strong>
            <span>
              Set by your sandbox admin.
              {plan.appliesOnWake ? ` Your sandbox still runs with ${plan.runningName ? `the ${plan.runningName} plan’s` : 'its previous'} limits until it next sleeps and wakes.` : ''}
              {sandbox?.idleMinutes ? ` It sleeps after ${sandbox.idleMinutes} minutes without use.` : ''}
            </span>
          </span>
          <Space wrap>
            <PlanChangeButton size="middle" />
            <Button onClick={() => navigate('/')}>Usage and limits</Button>
          </Space>
        </div>
        <div id="set-sandbox-type"><SandboxTypePicker onChanged={() => refreshSandbox()} /></div>
        <DockerImageCache />
        <ManageSandboxSection onChanged={refreshSandbox} />
      </section>
      <div id="set-backups"><MyBackups initiallyOpen /></div>
    </>
  ),
  };
  const tabItems = SETTINGS_TABS.filter((item) => panes[item.key]).map((item) => ({ ...item, children: panes[item.key] }));
  // A link to a tab not shown here (no notification settings): General.
  const shownTab = tabItems.some((item) => item.key === tab) ? tab : 'general';

  return (
    <div className="sbx-home">
      <div className="sbx-home-inner settings">
        <div className="sbx-home-title">
          <h1>Settings</h1>
          <p>Your preferences, the same in every browser.</p>
        </div>
        {saveError && <div className="sbx-card sbx-home-error" role="alert">{saveError}</div>}
        <Tabs activeKey={shownTab} className="settings-tabs" items={tabItems} onChange={(key) => navigate(`/settings#${key}`, { replace: true })} />
      </div>
    </div>
  );
}
